// RAPIFAST :: Estado DEMO compartido de verificación (SOLO navegador).
// Persiste en localStorage para que lo que carga el conductor/negocio aparezca
// en el panel del administrador (incluso tras cambiar de sesión en el mismo
// navegador). NO es almacenamiento seguro ni base de datos: es exclusivamente
// para probar el flujo del piloto. Se reemplazará por Supabase + Storage privado.
import { createContext, useContext, useCallback, useEffect, useState } from "react";
import {
  MOCK_DRIVERS, MOCK_BUSINESSES, buildSelfDriver, buildSelfBusiness,
  SELF_DRIVER_ID, SELF_BUSINESS_ID,
} from "@/lib/verificationMock";

const STORAGE_KEY = "rapifast_demo_verification_v1";
const VerificationDemoContext = createContext(null);

function seed() {
  return {
    drivers: [buildSelfDriver(), ...MOCK_DRIVERS],
    businesses: [buildSelfBusiness(), ...MOCK_BUSINESSES],
  };
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seed();
    const parsed = JSON.parse(raw);
    if (!parsed.drivers || !parsed.businesses) return seed();
    // garantiza que los registros "self" existan
    if (!parsed.drivers.some((d) => d.id === SELF_DRIVER_ID)) parsed.drivers.unshift(buildSelfDriver());
    if (!parsed.businesses.some((b) => b.id === SELF_BUSINESS_ID)) parsed.businesses.unshift(buildSelfBusiness());
    return parsed;
  } catch {
    return seed();
  }
}

export function VerificationDemoProvider({ children }) {
  const [state, setState] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // posible exceso de cuota por archivos adjuntos grandes: guarda sin dataUrl
      try {
        const slim = {
          drivers: state.drivers.map((d) => ({
            ...d,
            docs: Object.fromEntries(Object.entries(d.docs).map(([k, v]) => [k, v.file ? { ...v, file: { ...v.file, dataUrl: null, tooLarge: true } } : v])),
          })),
          businesses: state.businesses.map((b) => ({
            ...b,
            supports: (b.supports || []).map((s) => (s.dataUrl ? { ...s, dataUrl: null, tooLarge: true } : s)),
          })),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
      } catch {
        /* ignore */
      }
    }
  }, [state]);

  // sincroniza entre pestañas / cambios de sesión en el mismo navegador
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try { setState(JSON.parse(e.newValue)); } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const updateDriver = useCallback((id, patch) => {
    setState((s) => ({ ...s, drivers: s.drivers.map((d) => (d.id === id ? { ...d, ...patch } : d)) }));
  }, []);
  const updateBusiness = useCallback((id, patch) => {
    setState((s) => ({ ...s, businesses: s.businesses.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
  }, []);
  const setDriverDoc = useCallback((id, docKey, patch) => {
    setState((s) => ({
      ...s,
      drivers: s.drivers.map((d) =>
        d.id === id ? { ...d, docs: { ...d.docs, [docKey]: { ...d.docs[docKey], ...patch } } } : d
      ),
    }));
  }, []);
  const resetDemo = useCallback(() => setState(seed()), []);

  const value = {
    drivers: state.drivers,
    businesses: state.businesses,
    driverById: (id) => state.drivers.find((d) => d.id === id),
    businessById: (id) => state.businesses.find((b) => b.id === id),
    selfDriver: () => state.drivers.find((d) => d.id === SELF_DRIVER_ID),
    selfBusiness: () => state.businesses.find((b) => b.id === SELF_BUSINESS_ID),
    updateDriver, updateBusiness, setDriverDoc, resetDemo,
  };
  return <VerificationDemoContext.Provider value={value}>{children}</VerificationDemoContext.Provider>;
}

export function useVerificationDemo() {
  const ctx = useContext(VerificationDemoContext);
  if (!ctx) throw new Error("useVerificationDemo debe usarse dentro de VerificationDemoProvider");
  return ctx;
}

// Lee un File como data URL (demo, solo navegador). Cap ~1.8MB para no reventar localStorage.
export function fileToDemoRecord(file) {
  return new Promise((resolve, reject) => {
    const MAX = 1.8 * 1024 * 1024;
    const base = { fileName: file.name, fileType: file.type || "application/octet-stream", size: file.size, uploadedAt: new Date().toISOString() };
    if (file.size > MAX) {
      resolve({ ...base, dataUrl: null, tooLarge: true });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve({ ...base, dataUrl: reader.result, tooLarge: false });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
